import React, { useEffect, useState } from 'react';
import Plot from 'react-plotly.js';
import axiosInstance from '../utils/axiosInstance';
import { plotlyStyle } from '../utils/plotlyStyle';
import {
  Stack,
  Button,
  Typography,
  TextField,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/ja';
import { MESSAGES } from '../constants/messages';

const ChartPriceRangeSales = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [step, setStep] = useState(10000);

  const now = dayjs();

  const defaultFrom = now
    .subtract(2, 'month')
    .startOf('month');

  const defaultTo = now.endOf('month');

  const [from, setFrom] = useState(defaultFrom);
  const [to, setTo] = useState(defaultTo);

  const fetchData = async (params = {}) => {
    setLoading(true);

    try {
      const res = await axiosInstance.get(
        '/chart/price-range-sales',
        { params }
      );

      setData(res.data);
      setError(null);
    } catch (err) {
      setError('データの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData({
      from: defaultFrom.format('YYYY-MM-DD'),
      to: defaultTo.format('YYYY-MM-DD'),
      step,
    });
  }, []);

  const applyFilter = () => {
    fetchData({
      from: from.startOf('month').format('YYYY-MM-DD'),
      to: to.endOf('month').format('YYYY-MM-DD'),
      step,
    });
  };

  const setThisMonth = () => {
    const f = now.startOf('month');
    const t = now.endOf('month');

    setFrom(f);
    setTo(t);

    fetchData({
      from: f.format('YYYY-MM-DD'),
      to: t.format('YYYY-MM-DD'),
      step,
    });
  };

  const setLast3Months = () => {
    const f = now
      .subtract(2, 'month')
      .startOf('month');

    const t = now.endOf('month');

    setFrom(f);
    setTo(t);

    fetchData({
      from: f.format('YYYY-MM-DD'),
      to: t.format('YYYY-MM-DD'),
      step,
    });
  };

  const setLast12Months = () => {
    const f = now
      .subtract(11, 'month')
      .startOf('month');

    const t = now.endOf('month');

    setFrom(f);
    setTo(t);

    fetchData({
      from: f.format('YYYY-MM-DD'),
      to: t.format('YYYY-MM-DD'),
      step,
    });
  };

  if (loading) {
    return (
      <Typography>
        {MESSAGES.LOADING}
      </Typography>
    );
  }

  if (error) {
    return (
      <Typography>
        {error || MESSAGES.LOAD_FAILED}
      </Typography>
    );
  }

  const plotData = {
    x: data.map(item => item.price_range),

    y: data.map(item =>
      Number(item.total_sales)
    ),

    type: 'bar',

    name: '価格帯別売上',
    marker: {
      color: plotlyStyle.colors.bar,
    },

    text: data.map(
      item =>
        `${Number(
          item.total_sales
        ).toLocaleString()}円`
    ),

    hovertemplate:
      '価格帯: %{x}<br>' +
      '売上: %{y:,.0f}円' +
      '<extra></extra>',
  };

  const layout = {
    ...plotlyStyle.layout,

    title: {
      text: '価格帯別売上',
    },

    xaxis: {
      ...plotlyStyle.layout.xaxis,
      title: '価格帯',
    },

    yaxis: {
      ...plotlyStyle.layout.yaxis,
      title: '売上高 (円)',
      tickformat: ',.0f',
    },
  };

  return (
    <>
      <LocalizationProvider
        dateAdapter={AdapterDayjs}
        adapterLocale="ja"
      >
        <Stack spacing={1.5} sx={{ mb: 2 }}>
          <TextField
            label="価格帯刻み幅"
            type="number"
            value={step}
            onChange={e =>
              setStep(
                Math.max(1, Number(e.target.value) || 1)
              )
            }
          />

          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
          >
            <DatePicker
              label="開始月"
              views={['year', 'month']}
              value={from}
              onChange={setFrom}
              maxDate={to}
              format="YYYY年M月"
              slotProps={{
                textField: {
                  size: 'small',
                },
              }}
            />

            <Typography>〜</Typography>

            <DatePicker
              label="終了月"
              views={['year', 'month']}
              value={to}
              onChange={setTo}
              minDate={from}
              format="YYYY年M月"
              slotProps={{
                textField: {
                  size: 'small',
                },
              }}
            />

            <Button
              size="small"
              variant="contained"
              onClick={applyFilter}
            >
              適用
            </Button>
          </Stack>

          <Stack
            direction="row"
            spacing={1}
          >
            <Button
              size="small"
              variant="outlined"
              onClick={setThisMonth}
            >
              今月
            </Button>

            <Button
              size="small"
              variant="outlined"
              onClick={setLast3Months}
            >
              直近3ヶ月
            </Button>

            <Button
              size="small"
              variant="outlined"
              onClick={setLast12Months}
            >
              直近12ヶ月
            </Button>
          </Stack>
        </Stack>
      </LocalizationProvider>

      <div>
        <Plot
          data={[plotData]}
          layout={layout}
          style={{
            width: '100%',
            height: '100%',
            minHeight: 500,
          }}
          useResizeHandler={true}
        />
      </div>
    </>
  );
};

export default ChartPriceRangeSales;